import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Plane, Search, Loader2, AlertCircle, ArrowRight, PlaneTakeoff, PlaneLanding, Clock,
  Building2, Zap, CloudSun, Wind, Eye, Droplets, Umbrella, RefreshCw, Info,
} from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FORECAST = 'https://api.open-meteo.com/v1/forecast';
const GEOCODE = 'https://geocoding-api.open-meteo.com/v1/search';

const SEARCH_TYPES = [
  { id: 'flight', label: 'Flight number', hint: 'IATA/ICAO e.g. EK202, UAE202' },
  { id: 'airport', label: 'Airport', hint: 'IATA (3-letter) or ICAO (4-letter) e.g. DXB, KJFK' },
  { id: 'airline', label: 'Airline', hint: 'IATA (2-letter) or ICAO (3-letter) e.g. EK, UAE' },
];

const DEMO_FLIGHT = {
  ident: 'EK202', ident_iata: 'EK202', ident_icao: 'UAE202',
  origin: { code: 'OMDB', code_iata: 'DXB', city: 'Dubai', name: 'Dubai Intl' },
  destination: { code: 'KJFK', code_iata: 'JFK', city: 'New York', name: 'John F. Kennedy Intl' },
  scheduled_out: '2026-02-19T08:15:00Z', estimated_out: '2026-02-19T08:35:00Z', actual_out: null,
  scheduled_in: '2026-02-19T14:05:00Z', estimated_in: '2026-02-19T14:32:00Z', actual_in: null,
  status: 'En Route', progress_percent: 42, departure_delay: 1200, arrival_delay: 1620,
  last_position: { latitude: 45.2, longitude: -25.9, altitude: 38000, groundspeed: 490, timestamp: '2026-02-19T10:30:00Z' },
  aircraft_type: 'A380',
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—';
const delayMin = (s) => (s ? Math.round(s / 60) : 0);
const statusTint = (s) => {
  const u = (s || '').toLowerCase();
  if (u.includes('cancel')) return 'rose';
  if (u.includes('delay') || u.includes('divert')) return 'amber';
  if (u.includes('arrived') || u.includes('landed')) return 'emerald';
  if (u.includes('route') || u.includes('scheduled') || u.includes('taxi') || u.includes('active')) return 'cyan';
  return 'slate';
};

const Section = ({ title, children, right, testId }) => (
  <div className="glass-card rounded-2xl border border-slate-800 p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <h3 className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const codeInfo = { 0: 'Clear', 1: 'Mostly clear', 2: 'Partly cloudy', 3: 'Overcast', 45: 'Fog', 48: 'Fog', 51: 'Drizzle', 61: 'Rain', 63: 'Rain', 65: 'Heavy rain', 71: 'Snow', 80: 'Showers', 95: 'Thunderstorm' };

const AirportWeather = ({ airport, label }) => {
  const [wx, setWx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadWx = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const cityQuery = airport?.city || airport?.name || airport?.code_iata || airport?.code;
      if (!cityQuery) throw new Error('No location');
      const geo = await axios.get(GEOCODE, { params: { name: cityQuery, count: 1 } });
      const g = geo.data?.results?.[0];
      if (!g) throw new Error('Could not geocode airport city');
      const wxRes = await axios.get(FORECAST, {
        params: {
          latitude: g.latitude, longitude: g.longitude,
          current: 'temperature_2m,precipitation,cloud_cover,wind_speed_10m,visibility,weather_code',
          timezone: 'auto', wind_speed_unit: 'kmh',
        },
      });
      setWx({ ...wxRes.data.current, _place: cityQuery });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [airport]);
  useEffect(() => { loadWx(); }, [loadWx]);

  return (
    <div className="p-4 rounded border border-slate-800 bg-slate-950/40 space-y-2" data-testid={`airport-wx-${label}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label} · {airport?.code_iata || airport?.code}</span>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
      </div>
      {error && <div className="text-rose-300 font-mono text-[10px]">{error}</div>}
      {wx && (
        <>
          <div className="flex items-baseline gap-2">
            <span className="font-display font-black text-2xl text-[#00f0ff]">{Math.round(wx.temperature_2m)}°</span>
            <span className="font-mono text-[10px] text-slate-400">{codeInfo[wx.weather_code] || '—'}</span>
          </div>
          <div className="grid grid-cols-2 gap-1 text-[10px] font-mono text-slate-400">
            <span><Wind className="w-3 h-3 inline mr-1 text-[#ff9e00]" />{wx.wind_speed_10m.toFixed(0)} km/h</span>
            <span><Eye className="w-3 h-3 inline mr-1" />{(wx.visibility / 1000).toFixed(1)} km</span>
            <span><Droplets className="w-3 h-3 inline mr-1" />{wx.cloud_cover}% cloud</span>
            <span><Umbrella className="w-3 h-3 inline mr-1 text-[#39ff14]" />{wx.precipitation.toFixed(1)} mm</span>
          </div>
        </>
      )}
    </div>
  );
};

const FlightWeatherImpact = ({ origin, destination }) => {
  // Recompute a coarse impact from each airport's weather; can only fetch once
  // AirportWeather has, so we mirror the same logic client-side lightly here.
  const [combined, setCombined] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const fetchWx = async (a) => {
          const cityQuery = a?.city || a?.name || a?.code;
          if (!cityQuery) return null;
          const geo = await axios.get(GEOCODE, { params: { name: cityQuery, count: 1 } });
          const g = geo.data?.results?.[0];
          if (!g) return null;
          const w = await axios.get(FORECAST, {
            params: { latitude: g.latitude, longitude: g.longitude, current: 'wind_speed_10m,visibility,precipitation,weather_code', wind_speed_unit: 'kmh' },
          });
          return w.data.current;
        };
        const [o, d] = await Promise.all([fetchWx(origin), fetchWx(destination)]);
        const factors = [];
        let score = 0;
        [['Departure', o], ['Arrival', d]].forEach(([lbl, c]) => {
          if (!c) return;
          if (c.wind_speed_10m >= 40) { score += 2; factors.push(`${lbl} wind ${c.wind_speed_10m.toFixed(0)} km/h`); }
          else if (c.wind_speed_10m >= 25) { score += 1; factors.push(`${lbl} moderate wind ${c.wind_speed_10m.toFixed(0)} km/h`); }
          if ((c.visibility ?? 10000) < 3000) { score += 2; factors.push(`${lbl} low visibility ${(c.visibility / 1000).toFixed(1)} km`); }
          if (c.precipitation >= 2) { score += 1; factors.push(`${lbl} active precipitation ${c.precipitation.toFixed(1)} mm`); }
          if ([65, 82, 95, 96, 99].includes(c.weather_code)) { score += 2; factors.push(`${lbl} ${codeInfo[c.weather_code] || 'severe weather'}`); }
        });
        if (factors.length === 0) factors.push('Calm conditions at both endpoints');
        const level = score >= 4 ? 'High' : score >= 2 ? 'Moderate' : 'Low';
        setCombined({ level, factors });
      } catch { /* keep null on error */ }
    })();
  }, [origin, destination]);

  if (!combined) return null;
  const tint = combined.level === 'High' ? 'rose' : combined.level === 'Moderate' ? 'amber' : 'emerald';
  return (
    <div className={`p-4 rounded border border-${tint}-500/40 bg-${tint}-500/5 space-y-2`} data-testid="flight-weather-impact">
      <div className="flex items-center gap-2">
        <Zap className={`w-4 h-4 text-${tint}-400`} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Travel Impact (weather)</span>
      </div>
      <div className={`font-display font-black text-2xl text-${tint}-300 uppercase`}>{combined.level}</div>
      <ul className="space-y-0.5">
        {combined.factors.map((f, i) => <li key={i} className="font-mono text-[10px] text-slate-400">· {f}</li>)}
      </ul>
      <div className="font-mono text-[9px] text-slate-500 italic pt-1 border-t border-slate-900">
        Weather conditions may contribute to additional delays — this is a heuristic, not a confirmed cause.
      </div>
    </div>
  );
};

const FlightRow = ({ f, onOpen }) => {
  const dep = f.origin || {};
  const arr = f.destination || {};
  const status = f.status || '—';
  const tint = statusTint(status);
  return (
    <button onClick={() => onOpen(f)} className="w-full text-left p-4 border border-slate-800 rounded hover:border-[#00f0ff]/50 hover:bg-[#00f0ff]/5 transition-all cursor-pointer" data-testid={`flight-row-${f.ident}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-sm text-white">{f.ident || f.ident_iata}</span>
            <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-${tint}-500/40 bg-${tint}-500/10 text-${tint}-300`}>{status}</span>
          </div>
          <div className="font-mono text-[10px] text-slate-400 flex items-center gap-1.5 mt-0.5 truncate">
            <span>{dep.code_iata || dep.code}</span><ArrowRight className="w-3 h-3" /><span>{arr.code_iata || arr.code}</span>
            <span className="text-slate-600">· {fmt(f.scheduled_out)}</span>
          </div>
        </div>
        <span className="font-mono text-[9px] text-slate-500">
          {delayMin(f.departure_delay) > 0 && <span className="text-amber-400">+{delayMin(f.departure_delay)}m</span>}
        </span>
      </div>
    </button>
  );
};

export const FlightsView = () => {
  const [type, setType] = useState('flight');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState({ configured: false, demo_mode: true });
  const [flights, setFlights] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastFetch, setLastFetch] = useState(null);

  useEffect(() => { axios.get(`${API}/flights/status`).then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const isDemo = !status.configured;

  const search = async (e) => {
    e && e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true); setError(''); setFlights([]); setSelected(null);
    if (isDemo) {
      setFlights([DEMO_FLIGHT, { ...DEMO_FLIGHT, ident: 'UAL200', origin: { code: 'KLAX', code_iata: 'LAX', city: 'Los Angeles' }, destination: { code: 'RJTT', code_iata: 'HND', city: 'Tokyo' }, status: 'Scheduled', departure_delay: 0 }]);
      setLoading(false);
      setLastFetch(new Date());
      return;
    }
    try {
      let path;
      if (type === 'flight') path = `/flights/flight/${encodeURIComponent(q)}`;
      else if (type === 'airport') path = `/flights/airport/${encodeURIComponent(q)}`;
      else path = `/flights/operator/${encodeURIComponent(q)}`;
      const res = await axios.get(`${API}${path}`);
      const list = res.data?.flights || res.data?.arrivals || res.data?.departures || [];
      const flat = Array.isArray(res.data) ? res.data : (list.length ? list : (res.data?.scheduled ? [...(res.data.scheduled || []), ...(res.data.arrivals || []), ...(res.data.departures || [])] : []));
      const merged = res.data?.arrivals || res.data?.departures || res.data?.scheduled
        ? [...(res.data.scheduled || []), ...(res.data.departures || []), ...(res.data.arrivals || [])]
        : (Array.isArray(list) ? list : flat);
      const items = merged.length ? merged : (Array.isArray(res.data?.flights) ? res.data.flights : []);
      setFlights(items);
      setLastFetch(new Date());
      if (items.length === 0) setError('No flights returned for that query.');
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const openFlight = (f) => setSelected(f);

  const heading = useMemo(() => {
    if (selected) return `${selected.ident} · ${(selected.origin?.code_iata || selected.origin?.code || '—')} → ${(selected.destination?.code_iata || selected.destination?.code || '—')}`;
    if (flights.length) return `${flights.length} result${flights.length === 1 ? '' : 's'}`;
    return null;
  }, [selected, flights]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8" data-testid="flights-view">
      <section className="space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#ff9e00]/10 border border-[#ff9e00]/25 rounded-full">
          <Plane className="w-3 h-3 text-[#ff9e00]" />
          <span className="font-mono text-[10px] text-[#ff9e00] tracking-widest font-semibold uppercase">03 // USEFUL DATA · FLIGHT INTELLIGENCE</span>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">
          Flight <span className="text-[#ff9e00]">Intelligence</span>
        </h2>
        <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed max-w-2xl">
          Live flight status, schedule, position and airport ops via <a href="https://aviationstack.com/" target="_blank" rel="noreferrer" className="text-[#ff9e00] hover:underline">Aviationstack</a>. When a flight is selected we cross-reference the origin and destination weather to synthesize a travel-impact score.
        </p>
        {isDemo && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 px-3 py-2 text-[11px] font-mono flex items-start gap-2" data-testid="flights-demo-banner">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span><strong>DEMO MODE</strong> — no Aviationstack key configured. An admin must set the key in Admin Telemetry → Flight Pipeline Settings. Data below is synthetic and clearly labelled.</span>
          </div>
        )}
      </section>

      <form onSubmit={search} className="glass-card rounded-2xl border border-slate-800 p-4 space-y-2" data-testid="flights-search-form">
        <div className="flex flex-wrap items-center gap-2">
          {SEARCH_TYPES.map((s) => (
            <button key={s.id} type="button" onClick={() => setType(s.id)} className={`px-3 py-1 rounded font-mono text-[10px] uppercase tracking-widest font-bold transition-all cursor-pointer ${type === s.id ? 'bg-[#ff9e00]/15 border border-[#ff9e00]/40 text-[#ff9e00]' : 'border border-slate-800 text-slate-400 hover:text-slate-200'}`} data-testid={`flight-type-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={SEARCH_TYPES.find((s) => s.id === type)?.hint || 'Search…'} className="w-full bg-slate-950/60 border border-slate-800 focus:border-[#ff9e00]/60 rounded pl-9 pr-3 py-2 text-sm text-slate-200 font-mono outline-none" data-testid="input-flight-search" />
          </div>
          <button type="submit" disabled={loading || !query.trim()} className="px-4 py-2 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#ff9e00]/15 border border-[#ff9e00]/40 text-[#ff9e00] hover:bg-[#ff9e00] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer" data-testid="btn-flight-search">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}<span>Search</span>
          </button>
        </div>
      </form>

      {error && <div className="rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 px-3 py-2 text-xs font-mono flex items-start gap-2" data-testid="flights-error"><AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{error}</span></div>}
      {loading && <div className="glass-card rounded-2xl border border-slate-800 p-8 flex items-center justify-center text-slate-500 font-mono text-xs gap-2" data-testid="flights-loading"><Loader2 className="w-4 h-4 animate-spin" /> Querying AeroAPI…</div>}

      {!loading && flights.length > 0 && !selected && (
        <Section title={`Results${heading ? ' · ' + heading : ''}`} testId="flights-results">
          <div className="space-y-2">
            {flights.map((f, i) => <FlightRow key={f.fa_flight_id || `${f.ident}-${i}`} f={f} onOpen={openFlight} />)}
          </div>
        </Section>
      )}

      {!loading && flights.length === 0 && !error && !selected && (
        <div className="glass-card rounded-2xl border border-dashed border-slate-800 p-10 text-center text-slate-500 font-mono text-xs" data-testid="flights-empty">
          Enter a flight number, airport, or airline to begin.
        </div>
      )}

      {selected && (
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(null)} className="px-3 py-1.5 rounded border border-slate-800 hover:border-[#ff9e00]/50 hover:bg-[#ff9e00]/5 text-slate-300 font-mono text-[10px] uppercase tracking-widest font-bold cursor-pointer" data-testid="btn-back-to-results">← Back to results</button>
          </div>

          <Section title={heading} right={<span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border border-${statusTint(selected.status)}-500/40 bg-${statusTint(selected.status)}-500/10 text-${statusTint(selected.status)}-300`}>{selected.status || '—'}</span>} testId="flight-detail">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded border border-slate-800 bg-slate-950/40 space-y-2">
                <div className="flex items-center gap-2"><PlaneTakeoff className="w-4 h-4 text-[#00f0ff]" /><span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Departure</span></div>
                <div className="text-white font-display font-bold">{selected.origin?.name || selected.origin?.code} <span className="font-mono text-[10px] text-slate-500">({selected.origin?.code_iata || selected.origin?.code})</span></div>
                <div className="font-mono text-[10px] text-slate-400"><Clock className="w-3 h-3 inline mr-1" />Sched · {fmt(selected.scheduled_out)}</div>
                <div className="font-mono text-[10px] text-slate-400"><Clock className="w-3 h-3 inline mr-1" />Est · {fmt(selected.estimated_out)}</div>
                {delayMin(selected.departure_delay) > 0 && <div className="font-mono text-[10px] text-amber-400">Delay · +{delayMin(selected.departure_delay)} min</div>}
              </div>
              <div className="p-4 rounded border border-slate-800 bg-slate-950/40 space-y-2">
                <div className="flex items-center gap-2"><PlaneLanding className="w-4 h-4 text-[#ff9e00]" /><span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Arrival</span></div>
                <div className="text-white font-display font-bold">{selected.destination?.name || selected.destination?.code} <span className="font-mono text-[10px] text-slate-500">({selected.destination?.code_iata || selected.destination?.code})</span></div>
                <div className="font-mono text-[10px] text-slate-400"><Clock className="w-3 h-3 inline mr-1" />Sched · {fmt(selected.scheduled_in)}</div>
                <div className="font-mono text-[10px] text-slate-400"><Clock className="w-3 h-3 inline mr-1" />Est · {fmt(selected.estimated_in)}</div>
                {delayMin(selected.arrival_delay) > 0 && <div className="font-mono text-[10px] text-amber-400">Delay · +{delayMin(selected.arrival_delay)} min</div>}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="p-2 rounded border border-slate-800 bg-slate-950/40"><div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Aircraft</div><div className="text-white font-display font-bold text-xs">{selected.aircraft_type || '—'}</div></div>
              {selected.last_position && (
                <>
                  <div className="p-2 rounded border border-slate-800 bg-slate-950/40"><div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Altitude</div><div className="text-white font-display font-bold text-xs">{selected.last_position.altitude ? `${selected.last_position.altitude} ft` : '—'}</div></div>
                  <div className="p-2 rounded border border-slate-800 bg-slate-950/40"><div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Ground speed</div><div className="text-white font-display font-bold text-xs">{selected.last_position.groundspeed ? `${selected.last_position.groundspeed} kt` : '—'}</div></div>
                  <div className="p-2 rounded border border-slate-800 bg-slate-950/40"><div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Position</div><div className="text-white font-display font-bold text-xs">{selected.last_position.latitude.toFixed(2)}, {selected.last_position.longitude.toFixed(2)}</div></div>
                </>
              )}
            </div>
          </Section>

          {(selected.origin || selected.destination) && (
            <Section title="Flight + Weather" testId="flight-weather-section">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {selected.origin && <AirportWeather airport={selected.origin} label="Departure" />}
                {selected.destination && <AirportWeather airport={selected.destination} label="Arrival" />}
                <FlightWeatherImpact origin={selected.origin} destination={selected.destination} />
              </div>
              <div className="font-mono text-[9px] text-slate-500 border-t border-slate-900 pt-2 flex items-center gap-1">
                <CloudSun className="w-3 h-3" /> Weather data · <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" className="text-[#00f0ff] hover:underline">Open-Meteo</a>
              </div>
            </Section>
          )}
        </>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] font-mono text-slate-500 border-t border-slate-900 pt-3">
        <span>Flight source · <a href="https://aviationstack.com/" target="_blank" rel="noreferrer" className="text-[#ff9e00] hover:underline">Aviationstack</a> {isDemo && '· demo mode active'}</span>
        <div className="flex items-center gap-2">
          {lastFetch && <span>Last · {lastFetch.toLocaleTimeString()}</span>}
          {flights.length > 0 && <button onClick={search} className="px-2 py-0.5 rounded border border-slate-800 hover:border-[#ff9e00]/50 text-slate-400 hover:text-[#ff9e00] flex items-center gap-1 cursor-pointer" data-testid="btn-flights-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>}
        </div>
      </div>
    </motion.div>
  );
};
